// `unknown` here breaks every list_* method in Brain.ts: TS requires a source type to carry its
// own index signature to satisfy one structurally, and the generated ListXParams interfaces
// (query params) don't declare one — passing e.g. ListCustomersParams as this type would then
// fail to type-check at all ~16 call sites for no practical safety gain (verified).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type QueryParamsType = Record<string | number, any>;
export type ResponseFormat = keyof Omit<Body, "body" | "bodyUsed">;

export interface FullRequestParams extends Omit<RequestInit, "body"> {
  /** set parameter to `true` for call `securityWorker` for this request */
  secure?: boolean;
  /** request path */
  path: string;
  /** content type of request body */
  type?: ContentType;
  /** query params */
  query?: QueryParamsType;
  /** format of response (i.e. response.json() -> format: "json") */
  format?: ResponseFormat;
  /** request body */
  body?: unknown;
  /** base url */
  baseUrl?: string;
  /** request cancellation token */
  cancelToken?: CancelToken;
}

export type RequestParams = Omit<FullRequestParams, "body" | "method" | "query" | "path">;

export interface ApiConfig<SecurityDataType = unknown> {
  baseUrl?: string;
  baseApiParams?: Omit<RequestParams, "baseUrl" | "cancelToken" | "signal">;
  securityWorker?: (securityData: SecurityDataType | null) => Promise<RequestParams | void> | RequestParams | void;
  customFetch?: typeof fetch;
}

export interface HttpResponse<D, E = unknown> extends Response {
  data: D;
  error: E;
  json: () => Promise<D>;
}

type CancelToken = symbol | string | number;

export enum ContentType {
  Json = "application/json",
  FormData = "multipart/form-data",
  UrlEncoded = "application/x-www-form-urlencoded",
  Text = "text/plain",
}

export class HttpClient<SecurityDataType = unknown> {
  public baseUrl: string = "";
  private securityData: SecurityDataType | null = null;
  private securityWorker?: ApiConfig<SecurityDataType>["securityWorker"];
  private abortControllers = new Map<CancelToken, AbortController>();
  private customFetch = (...fetchParams: Parameters<typeof fetch>) => fetch(...fetchParams);

  private baseApiParams: RequestParams = {
    credentials: "include",  // ✅ Send cookies with requests (for httpOnly cookies)
    headers: {},
    redirect: "follow",
    referrerPolicy: "no-referrer",
  };

  constructor(apiConfig: ApiConfig<SecurityDataType> = {}) {
    Object.assign(this, apiConfig);
  }

  public setSecurityData = (data: SecurityDataType | null) => {
    this.securityData = data;
  };

  // ✅ AUTH: Token is now sent automatically via httpOnly cookie
  // No need to manually add Authorization header anymore
  // This function is kept for backward compatibility but returns empty object
  private getAuthHeaders = (): Record<string, string> => {
    // Token is sent automatically via httpOnly cookie (credentials: "include")
    // No longer reading from localStorage for security reasons
    return {};
  };

  // MULTI-TENANT: Get tenant ID from localStorage and add X-Tenant-ID header
  private getTenantHeaders = (): Record<string, string> => {
    const tenantId = localStorage.getItem('current_tenant_id');
    if (tenantId) {
      return {
        'X-Tenant-ID': tenantId
      };
    }
    return {};
  };

  // NEW: Get CSRF token from cookie
  private getCsrfToken = (): string | null => {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrf_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  };

  // NEW: Get CSRF headers for state-changing requests
  private getCsrfHeaders = (method?: string): Record<string, string> => {
    // Only add CSRF token for state-changing methods
    const protectedMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
    if (method && protectedMethods.includes(method.toUpperCase())) {
      const csrfToken = this.getCsrfToken();
      if (csrfToken) {
        return {
          'X-CSRF-Token': csrfToken
        };
      }
    }
    return {};
  };

  protected encodeQueryParam(key: string, value: unknown) {
    const encodedKey = encodeURIComponent(key);
    return `${encodedKey}=${encodeURIComponent(typeof value === "number" ? value : `${value}`)}`;
  }

  protected addQueryParam(query: QueryParamsType, key: string) {
    return this.encodeQueryParam(key, query[key]);
  }

  protected addArrayQueryParam(query: QueryParamsType, key: string) {
    const value = query[key];
    return value.map((v: unknown) => this.encodeQueryParam(key, v)).join("&");
  }

  protected toQueryString(rawQuery?: QueryParamsType): string {
    const query = rawQuery || {};
    const keys = Object.keys(query).filter((key) => "undefined" !== typeof query[key]);
    return keys
      .map((key) => (Array.isArray(query[key]) ? this.addArrayQueryParam(query, key) : this.addQueryParam(query, key)))
      .join("&");
  }

  protected addQueryParams(rawQuery?: QueryParamsType): string {
    const queryString = this.toQueryString(rawQuery);
    return queryString ? `?${queryString}` : "";
  }

  private contentFormatters: Record<ContentType, (input: unknown) => BodyInit> = {
    [ContentType.Json]: (input: unknown) =>
      (input !== null && (typeof input === "object" || typeof input === "string") ? JSON.stringify(input) : String(input)) as BodyInit,
    [ContentType.Text]: (input: unknown) => (input !== null && typeof input !== "string" ? JSON.stringify(input) : String(input)) as BodyInit,
    [ContentType.FormData]: (input: unknown) => {
      // If input is already a FormData instance, return it directly
      if (input instanceof FormData) {
        return input;
      }

      // Otherwise, build FormData from object
      const obj = (input || {}) as Record<string, unknown>;
      return Object.keys(obj).reduce((formData, key) => {
        const property = obj[key];

        if (Array.isArray(property)) {
          for (const it of property) {
            formData.append(
              key,
              it instanceof Blob ? it : typeof it === "object" && it !== null ? JSON.stringify(it) : `${it}`,
            );
          }
        } else {
          formData.append(
            key,
            property instanceof Blob
              ? property
              : typeof property === "object" && property !== null
              ? JSON.stringify(property)
              : `${property}`,
          );
        }

        return formData;
      }, new FormData());
    },
    [ContentType.UrlEncoded]: (input: unknown) => this.toQueryString(input as QueryParamsType),
  };

  protected mergeRequestParams(params1: RequestParams, params2?: RequestParams): RequestParams {
    // MULTI-TENANT: Automatically add tenant headers
    // AUTH: Token is sent automatically via httpOnly cookie (credentials: "include")
    const tenantHeaders = this.getTenantHeaders(); // MULTI-TENANT

    return {
      ...this.baseApiParams,
      ...params1,
      ...(params2 || {}),
      headers: {
        ...(this.baseApiParams.headers || {}),
        ...(params1.headers || {}),
        ...((params2 && params2.headers) || {}),
        // ✅ No Authorization header needed - token sent via httpOnly cookie
        ...tenantHeaders, // MULTI-TENANT: Add X-Tenant-ID header
      },
    };
  }

  protected createAbortSignal = (cancelToken: CancelToken): AbortSignal | undefined => {
    if (this.abortControllers.has(cancelToken)) {
      const abortController = this.abortControllers.get(cancelToken);
      if (abortController) {
        return abortController.signal;
      }
      return void 0;
    }

    const abortController = new AbortController();
    this.abortControllers.set(cancelToken, abortController);
    return abortController.signal;
  };

  public abortRequest = (cancelToken: CancelToken) => {
    const abortController = this.abortControllers.get(cancelToken);

    if (abortController) {
      abortController.abort();
      this.abortControllers.delete(cancelToken);
    }
  };

  public request = async <T = unknown, E = unknown>({
    body,
    secure,
    path,
    type,
    query,
    format,
    baseUrl,
    cancelToken,
    method,
    ...params
  }: FullRequestParams): Promise<HttpResponse<T, E>> => {
    const secureParams =
      ((typeof secure === "boolean" ? secure : this.baseApiParams.secure) &&
        this.securityWorker &&
        (await this.securityWorker(this.securityData))) ||
      {};
    const requestParams = this.mergeRequestParams(params, secureParams);
    const queryString = query && this.toQueryString(query);
    const payloadFormatter = this.contentFormatters[type || ContentType.Json];
    const responseFormat = format || requestParams.format;

    // MODIFIED: Add CSRF token for state-changing requests
    const csrfHeaders = this.getCsrfHeaders(method);

    return this.customFetch(`${baseUrl || this.baseUrl || ""}${path}${queryString ? `?${queryString}` : ""}`, {
      ...requestParams,
      method,
      headers: {
        ...(requestParams.headers || {}),
        ...(type && type !== ContentType.FormData ? { "Content-Type": type } : {}),
        ...csrfHeaders, // Add CSRF token
      },
      signal: (cancelToken ? this.createAbortSignal(cancelToken) : requestParams.signal) || null,
      body: typeof body === "undefined" || body === null ? null : payloadFormatter(body),
    }).then(async (response) => {
      const r = response as HttpResponse<T, E>;
      r.data = null as unknown as T;
      r.error = null as unknown as E;

      const data = !responseFormat
        ? r
        : await response[responseFormat]()
            .then((data) => {
              if (r.ok) {
                r.data = data;
              } else {
                r.error = data;
              }
              return r;
            })
            .catch((e) => {
              r.error = e;
              return r;
            });

      if (cancelToken) {
        this.abortControllers.delete(cancelToken);
      }

      // MODIFIED: Handle 401 Unauthorized - redirect to login
      if (response.status === 401) {
        localStorage.removeItem('access_token');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }

      if (!response.ok) throw data;
      return data;
    });
  };

  public async *requestStream<T = unknown>({
    body,
    secure,
    path,
    type,
    query,
    baseUrl,
    cancelToken,
    method,
    ...params
  }: FullRequestParams): AsyncIterable<T> {
    const secureParams =
      ((typeof secure === "boolean" ? secure : this.baseApiParams.secure) &&
        this.securityWorker &&
        (await this.securityWorker(this.securityData))) ||
      {};
    const requestParams = this.mergeRequestParams(params, secureParams);
    const queryString = query && this.toQueryString(query);
    const payloadFormatter = this.contentFormatters[type || ContentType.Json];

    // MODIFIED: Add CSRF token for state-changing requests
    const csrfHeaders = this.getCsrfHeaders(method);

    let response;
    try {
      response = await this.customFetch(
        `${baseUrl || this.baseUrl || ""}${path}${queryString ? `?${queryString}` : ""}`,
        {
          ...requestParams,
          method,
          headers: {
            ...(requestParams.headers || {}),
            ...(type && type !== ContentType.FormData ? { "Content-Type": type } : {}),
            ...csrfHeaders, // Add CSRF token
          },
          signal: (cancelToken ? this.createAbortSignal(cancelToken) : requestParams.signal) || null,
          body: typeof body === "undefined" || body === null ? null : payloadFormatter(body),
        },
      );

      if (!response.ok) {
        // Handle 401 in streams too
        if (response.status === 401) {
          localStorage.removeItem('access_token');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        throw new Error("Response not OK");
      }

      if (!response.body) {
        throw new Error("Response has no body");
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      const contentType = response.headers.get("Content-Type");

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        let data;
        if (contentType === "application/json") {
          const chunk = decoder.decode(value, { stream: true });
          try {
            data = JSON.parse(chunk);
          } catch (error) {
            throw error instanceof Error ? error : new Error(String(error));
          }
        } else if (contentType === "application/octet-stream") {
          data = new Uint8Array(value);
        } else {
          const chunk = decoder.decode(value, { stream: true });
          data = chunk;
        }

        yield data as T;
      }
    } catch (error) {
      throw error instanceof Error ? error : new Error(String(error));
    } finally {
      if (cancelToken) {
        this.abortControllers.delete(cancelToken);
      }
    }
  }
}